import React, { useState } from "react";
import { Button, Input, Modal, message } from "antd";
import Cookies from "js-cookie";
import { canAmendOffer } from "../utils/offerAmendment";
import { propertyLoanProcessingFeeTerms } from "../utils/propertyLoanProcessingFee";

const API_BASE = "https://www.smart-lend.com/api";

// Keep the editor at page level: loan card components remount when their page refreshes.
export function useLenderOfferAmendment() {
  const [selection, setSelection] = useState(null);
  return {
    amendmentModal: selection ? <AmendLenderOfferModal {...selection}
      key={`${selection.loanType}-${selection.offer.id}`} onClose={() => setSelection(null)} /> : null,
    renderAmendOfferButton: (props) => canAmendOffer(props.loanType, props.offer, props.loan) ? (
      <Button className="all_button sl-card-secondary-action" style={{ width: "100%", borderRadius: 30, marginTop: 10 }}
        onClick={(event) => { event.stopPropagation(); event.preventDefault(); setSelection(props); }}>
        Amend offer
      </Button>
    ) : null,
  };
}

function AmendLenderOfferModal({ loanType, offer, onUpdated, onClose }) {
  const [busy, setBusy] = useState(false);
  const corporate = loanType === "corporate";
  const terms = offer.offer_data || {};
  const [form, setForm] = useState(() => ({
    amount: corporate ? offer.offer_amount : terms.amount,
    rate: corporate ? Number(offer.offer_interest_rate) * 100 : terms.rate_pa,
    tenor: corporate ? offer.offer_period : terms.tenor_years,
    processingFee: Number(offer.processing_fee || 0) * 100,
    note: "",
  }));
  const [error, setError] = useState("");

  const update = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const amount = Number(form.amount);
    const rate = Number(form.rate);
    const tenor = Number(form.tenor);
    if (!(amount > 0) || form.rate === "" || !Number.isFinite(rate) || rate < 0 || !(tenor > 0)
      || (corporate && (!Number.isInteger(tenor) || Number(form.processingFee) < 0))) {
      setError("Enter a positive amount and tenor, and a non-negative rate and fee.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      let url;
      let payload;
      if (corporate) {
        const decimalRate = rate / 100;
        const monthlyRate = offer.type === "effective" ? decimalRate / 12 : decimalRate;
        const monthly = offer.type === "effective" && monthlyRate > 0
          ? amount * monthlyRate * (1 + monthlyRate) ** tenor / ((1 + monthlyRate) ** tenor - 1)
          : amount / tenor + (offer.type === "effective" ? 0 : amount * monthlyRate);
        const frequency = offer.type === "effective" ? 1
          : offer.repayment_tenor === "Weekly" ? 4 : offer.repayment_tenor === "Bi-Weekly" ? 2 : 1;
        const round = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
        const principal = round(monthly * tenor);
        url = `${API_BASE}/loan/updateOffer/${offer.id}`;
        payload = {
          offer_amount: amount, offer_interest_rate: decimalRate, offer_period: tenor,
          offer_monthly_installment: round(monthly / frequency), offer_principal: principal,
          offer_revenue: round(principal - amount - Number(offer.total_admin_fee || 0)),
          admin_fee: Number(offer.admin_fee || 0), total_admin_fee: Number(offer.total_admin_fee || 0),
          processing_fee: Number(form.processingFee) / 100,
        };
      } else {
        url = `${API_BASE}/${loanType === "personal" ? "personalLoans/offers/upsert" : "prop-loans/offers/upsert"}`;
        payload = {
          id: offer.id,
          [loanType === "personal" ? "personal_loan_request_id" : "prop_loan_request_id"]:
            offer[loanType === "personal" ? "personal_loan_request_id" : "prop_loan_request_id"] || offer.request?.id,
          offer_data: {
            ...offer.offer_data, amount, rate_pa: rate, tenor_years: tenor,
            ...(loanType === "property" ? propertyLoanProcessingFeeTerms(amount) : {}),
          },
          lender_message: form.note || null,
        };
      }
      const response = await fetch(url, {
        method: corporate ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${Cookies.get("token")}` },
        body: JSON.stringify(payload),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || "Unable to amend this offer.");
      onClose();
      message.success("Offer amended. The borrower can review the revised terms.");
      onUpdated?.(json.loan_offer || json.offer);
    } catch (err) {
      setError(err.message || "Unable to amend this offer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Amend offer" open footer={null} destroyOnClose
      onCancel={() => { if (!busy) onClose(); }}>
      <form onSubmit={submit}>
        <p>The borrower will need to review and accept the revised terms before the loan can start.</p>
        <label style={{ display: "block", marginBottom: 12 }}>
          Loan amount (SGD)
          <Input type="number" required min="0.01" step="0.01" value={form.amount ?? ""} onChange={update("amount")} />
        </label>
        <label style={{ display: "block", marginBottom: 12 }}>
          {corporate ? (offer.type === "effective" ? "Interest rate (% per annum)" : "Interest rate (% per month)") : "Interest rate (% p.a.)"}
          <Input type="number" required min="0" step="any" value={form.rate ?? ""} onChange={update("rate")} />
        </label>
        <label style={{ display: "block", marginBottom: 12 }}>
          {corporate ? "Tenor (months)" : "Tenor (years)"}
          <Input type="number" required min={corporate ? "1" : "0.000001"} step={corporate ? "1" : "any"}
            value={form.tenor ?? ""} onChange={update("tenor")} />
        </label>
        {corporate && <label style={{ display: "block", marginBottom: 12 }}>
          Processing fee (%)
          <Input type="number" required min="0" step="any" value={form.processingFee ?? ""} onChange={update("processingFee")} />
        </label>}
        {!corporate && <label style={{ display: "block", marginBottom: 12 }}>
          Message to borrower (optional)
          <Input.TextArea maxLength={2000} value={form.note} onChange={update("note")} />
        </label>}
        {error && <p role="alert" style={{ color: "#b42318" }}>{error}</p>}
        <Button htmlType="submit" type="primary" loading={busy}>Save amended offer</Button>
      </form>
    </Modal>
  );
}
